import React from 'react'
import InvoiceList from './InvoiceList'
import PaymentForm from './PaymentForm'
import { useMutation, useQuery } from 'react-apollo'
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import { UPDATE_PAYMENT_INTENT } from '../../queries/paymentIntent'
import { CREATE_DONATION_INVOICE, CREATE_EDUCATION_INVOICE, CREATE_SHOPPING_CART_INVOICES, CREATE_SHOPPING_INVOICE, INVOICES_BY_INVOICE_NUMBERS, INVOICES_BY_MEMBER_NUMBER, INVOICES_BY_PROXY_TOKEN } from '../../queries/invoices'
import { withAuthProvider } from '../../lib/auth'
import { withRouter } from 'react-router-dom'
import qs from 'query-string'
import DonationList from './DonationList'
import PaymentPanel from './PaymentPanel'
import ProductList from './ProductList'
import Legal from './Legal'
import { TotalLabel } from '../shared.styles'
import {
  isDonationNan,
  formatCurrency,
  mapAttributes,
  totalAmount,
  validateAmountFormat,
  toMoneyFormat,
  queryHasProducts,
  invoicesAmounts,
  getMemberNumbersFromSelectedItems,
  hasDuesSelected
} from '../../helpers'
import { donationFunds } from '../../helpers/constants'

const updateDonations = value => state => {
  const donations = state.donations.map(item => ({ ...item, value: validateAmountFormat(value) }))
  const amount = totalAmount({ ...state, donations })

  return {
    ...state,
    amount,
    donations
  }
}

const querySetup = (query, variables) => ({
  query,
  params: {
    variables
  }
})

const getInvoiceQuery = ({ memberNumber, invoiceNumbers, token }) => {
  if (token) {
    return querySetup(INVOICES_BY_PROXY_TOKEN, { token })
  } else if (invoiceNumbers) {
    return querySetup(INVOICES_BY_INVOICE_NUMBERS, { invoiceNumbers, open: true })
  }

  return querySetup(INVOICES_BY_MEMBER_NUMBER, { memberNumber, open: true })
}

const mapInvoiceAttributes = (invoices, attributes) => invoices.map(invoice => ({
  ...invoice,
  value: parseFloat(invoice.balance).toFixed(2),
  ...attributes
}))

const PaymentContainer = ({ me, authReady, location, history, isLoggedIn, paymentIntentId }) => {
  const { invoiceNumbers = '', defaultDonation = 0.00, redirectUrl, token, ...queryParams } = qs.parse(location.search)
  const initialDonations = mapAttributes(donationFunds, { value: validateAmountFormat(defaultDonation) })
  const hasProducts = queryHasProducts(queryParams)

  if (authReady && !isLoggedIn && !token) {
    history.push({
      pathname: '/login',
      search: history.location.search
    })
  }

  const stripe = useStripe()
  const elements = useElements()

  const [updatePaymentIntent] = useMutation(UPDATE_PAYMENT_INTENT)
  const [createDonationInvoice] = useMutation(CREATE_DONATION_INVOICE)
  const [createEducationInvoice] = useMutation(CREATE_EDUCATION_INVOICE)
  const [createShoppingCartInvoices] = useMutation(CREATE_SHOPPING_CART_INVOICES)
  const [createShoppingInvoice] = useMutation(CREATE_SHOPPING_INVOICE)

  const [formData, setFormData] = React.useState({
    memberNumber: '',
    email: '',
    amount: 0,
    formSubmitted: false,
    stripeError: {},
    invoices: [],
    selectedItems: [],
    donations: initialDonations,
    products: [],
    productsTotal: 0,
    token,
    redirectUrl
  })

  React.useEffect(() => {
    if (me) {
      const memberNumber = me.id.startsWith('ol:') ? me.id.slice(3) : me.id
      const email = me.primaryEmail ? me.primaryEmail : ''
      setFormData(state => ({ ...state, memberNumber, email }))
    }
  }, [me])

  const { query, params } = getInvoiceQuery({ memberNumber: formData.memberNumber, invoiceNumbers, token })
  const skip = !formData.memberNumber && !invoiceNumbers && !formData.token
  const { data, loading, error } = useQuery(query, { ...params, skip })

  React.useEffect(() => {
    if (data) {
      const { invoicesByInvoiceNumbers = [], invoicesByMemberNumber = [], invoicesByProxyToken = [] } = data
      const invoices = [
        ...mapInvoiceAttributes(invoicesByProxyToken, { removeable: false }),
        ...mapInvoiceAttributes(invoicesByInvoiceNumbers, { removeable: false }),
        ...mapInvoiceAttributes(invoicesByMemberNumber, { removeable: true })
      ]
      setFormData(state => {
        const selectedItems = invoiceNumbers || state.token ? invoices.map(invoice => ({ data: invoice })) : []
        const memberNumber = formData.memberNumber || (invoices[0] && invoices[0].memberNumber)
        const amount = totalAmount({ ...state, selectedItems })

        return {
          ...state,
          amount,
          invoices,
          selectedItems,
          memberNumber
        }
      })
    }
  }, [data, invoiceNumbers, formData.memberNumber])

  React.useEffect(() => {
    setFormData(state => ({ ...state, amount: totalAmount(state) }))
  }, [])

  React.useEffect(() => {
    if (defaultDonation) return
    if (hasDuesSelected(formData.selectedItems)) {
      setFormData(updateDonations(40))
    } else {
      setFormData(updateDonations(0))
    }
  }, [formData.selectedItems, defaultDonation])

  const handleChange = (id) => ({ target: { value } }) => {
    setFormData(state => ({
      ...state,
      [id]: value
    }))
  }

  const handleDonationChange = (fund) => ({ target: { value } }) => {
    if (typeof value === 'string' && value === '') value = 0

    if (isDonationNan(value)) return

    setFormData(state => {
      const donations = state.donations.map(item => item.fund === fund ? { ...item, value: validateAmountFormat(value) } : item)
      const amount = totalAmount({ ...state, donations })

      return {
        ...state,
        amount,
        donations
      }
    })
  }

  const handleInvoiceAmountChange = (invoiceNum) => ({ target: { value } }) => {
    setFormData(state => {
      const selectedItems = state.selectedItems.map((item) =>
        item.data.invoiceNum === invoiceNum
          ? { ...item, data: { ...item.data, value: validateAmountFormat(value, item.data.balance) } }
          : item
      )

      const invoices = invoiceNumbers || state.token
        ? state.invoices.map(invoice => invoice.invoiceNum === invoiceNum
          ? { ...invoice, value: validateAmountFormat(value, invoice.balance) }
          : invoice)
        : state.invoices

      const amount = totalAmount({ ...state, selectedItems })

      return {
        ...state,
        amount,
        invoices,
        selectedItems
      }
    })
  }

  const handleInvoiceListAdd = (item) => {
    setFormData(state => {
      const selectedItems = [...state.selectedItems, item]
      const amount = totalAmount({ ...state, selectedItems })
      return {
        ...state,
        amount,
        selectedItems
      }
    })
  }

  const handleInvoiceListRemove = (index) => {
    setFormData(state => {
      const selectedItems = [...state.selectedItems.slice(0, index), ...state.selectedItems.slice(index + 1)]
      const amount = totalAmount({ ...state, selectedItems })
      return {
        ...state,
        amount,
        selectedItems
      }
    })
  }

  const handleProductsQuery = React.useCallback((products) => {
    setFormData(state => {
      const productsTotal = products.reduce((acc, product) => acc + parseFloat(product.amount), 0.0)
      const amount = totalAmount({ ...state, productsTotal })
      return {
        ...state,
        amount,
        products,
        productsTotal
      }
    })
  }, [])

  const createInvoices = async (id, { memberNumber, donations, productsTotal, selectedItems }, { cartId, orderSerno, sessionSerno, token }) => {
    let generatedInvoices = []

    const memberNumbersFromSelectedItems = getMemberNumbersFromSelectedItems(selectedItems)
    const memberNumberForDonations = memberNumbersFromSelectedItems[0] || memberNumber
    for (const { fund, value } of donations) {
      if (value > 0) {
        const { data: { createDonationInvoice: { invoices } } } = await createDonationInvoice({ variables: { memberNumber: memberNumberForDonations, fund, amount: value } })
        generatedInvoices = [...generatedInvoices, ...invoices]
      }
    }

    if (cartId) {
      const { data: { createShoppingCartInvoices: { invoices } } } = await createShoppingCartInvoices({ variables: { cartId } })
      generatedInvoices = [...generatedInvoices, ...invoices]
    }
    if (orderSerno) {
      const resp = await createEducationInvoice({ variables: { orderSerno, amount: toMoneyFormat(productsTotal) } })
      const { data: { createEducationInvoice: { invoices, redirectUrl } } } = resp
      generatedInvoices = [...generatedInvoices, ...invoices]
      setFormData(state => ({ ...state, redirectUrl }))
    }
    if (sessionSerno) {
      const { data: { createShoppingInvoice: { invoices } } } = await createShoppingInvoice({ variables: { sessionSerno, memberNumber } })
      generatedInvoices = [...generatedInvoices, ...invoices]
    }

    return generatedInvoices
  }

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return
    }
    setFormData(state => ({ ...state, formSubmitted: true }))
    elements.getElement(PaymentElement)

    try {
      const selectedInvoices = formData.selectedItems.map(({ data }) => data)

      const allInvoices = await createInvoices(paymentIntentId, formData, queryParams)

      // Escape if no invoices will be paid for
      if (!selectedInvoices.length && !allInvoices.length) return history.push('/no-invoices')

      const { invoiceAmounts, invoiceNumbers } = invoicesAmounts([...selectedInvoices, ...allInvoices])

      const { data: updatedPaymentIntent } = await updatePaymentIntent({
        variables: {
          input: {
            id: paymentIntentId,
            amount: formData.amount,
            description: `Member Number - ${formData.memberNumber}. Invoice Numbers - ${invoiceNumbers}`,
            metadata: {
              invoices: invoiceNumbers,
              amounts: invoiceAmounts,
              email: formData.email,
              entityId: formData.memberNumber
            }
          }
        }
      })

      if (updatedPaymentIntent.error) {
        throw new Error(updatedPaymentIntent.error)
      }

      const response = await stripe.confirmPayment({
        elements,
        redirect: 'if_required'
      })

      if (response.error) {
        return setFormData((state) => ({
          ...state,
          formSubmitted: false,
          stripeError: response.error
        }))
      }

      // A successful credit card payment will have status of 'succeeded'. A successful ACH payment will have status of 'processing'.
      const redirectUrl = response.paymentIntent.status === 'succeeded' ? '/success?type=payment' : '/success?type=achPayment'
      formData.redirectUrl ? window.location.href = formData.redirectUrl : history.replace(redirectUrl)
    } catch (e) {
      setFormData(state => ({ ...state, formSubmitted: false }))
      history.push('/error')
    }
  }

  return (
    <PaymentPanel>
      {hasProducts
        ? (
          <ProductList
            products={formData.products}
            queryParams={queryParams}
            handleProductsQuery={handleProductsQuery}
          />
          )
        : (
          <InvoiceList
            formData={formData}
            handleInvoiceListRemove={handleInvoiceListRemove}
            handleInvoiceListAdd={handleInvoiceListAdd}
            handleInvoiceAmountChange={handleInvoiceAmountChange}
            invoiceNumbers={invoiceNumbers}
            error={error}
            loading={loading}
          />)}
      {(defaultDonation || hasDuesSelected(formData.selectedItems))
        ? (
          <DonationList
            donations={formData.donations}
            handleChange={handleDonationChange}
          />
          )
        : null}
      <TotalLabel>Total: {formatCurrency(formData.amount)}</TotalLabel>
      {formData.amount > 0 &&
        <PaymentForm
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
        />}
      <Legal
        dues={hasDuesSelected(formData.selectedItems)}
        contributions={defaultDonation || hasDuesSelected(formData.selectedItems)}
      />
    </PaymentPanel>
  )
}

export default withRouter(withAuthProvider(PaymentContainer))
